import { compact } from 'lodash-es';
import { InvoiceDisplayStatus, InvoiceDisplayStatusV2, InvoicePaymentDisplayStatusType, InvoiceSortingColumn, PaymentDetailType, ISSUES_PREVENTING_PAYMENT } from 'anchor-scout-common/types';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import { invoiceModel } from '~/models';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { getFormattedTimeRange } from './DateFilterPicker';
export var InvoiceHeaderItemID = /*#__PURE__*/ function(InvoiceHeaderItemID) {
    InvoiceHeaderItemID["invoiceId"] = "invoiceId";
    InvoiceHeaderItemID["companyName"] = "companyName";
    InvoiceHeaderItemID["status"] = "status";
    InvoiceHeaderItemID["issueDate"] = "issueDate";
    InvoiceHeaderItemID["dueDate"] = "dueDate";
    InvoiceHeaderItemID["total"] = "total";
    InvoiceHeaderItemID["additionalInformation"] = "additionalInformation";
    InvoiceHeaderItemID["paymentMethod"] = "paymentMethod";
    return InvoiceHeaderItemID;
}({});
export const invoiceHeaderItemIdToSortingColumn = {
    ["invoiceId"]: InvoiceSortingColumn.Number,
    ["companyName"]: InvoiceSortingColumn.CompanyName,
    ["status"]: InvoiceSortingColumn.Status,
    ["additionalInformation"]: InvoiceSortingColumn.PaymentStatus,
    ["issueDate"]: InvoiceSortingColumn.IssuedOn,
    ["dueDate"]: InvoiceSortingColumn.DueDate,
    ["total"]: InvoiceSortingColumn.TotalAmount
};
function invoiceDisplayStatusToLabel(texts) {
    return {
        [InvoiceDisplayStatus.Issued]: texts.issued(),
        [InvoiceDisplayStatus.Overdue]: texts.overdue(),
        [InvoiceDisplayStatus.PaymentInitiated]: texts.processing(),
        [InvoiceDisplayStatus.Paid]: texts.paid(),
        [InvoiceDisplayStatus.Voided]: texts.voided(),
        [InvoiceDisplayStatus.Refunded]: texts.refunded(),
        [InvoiceDisplayStatus.RefundFailed]: texts.refundFailed(),
        [InvoiceDisplayStatus.PaymentDisputed]: texts.disputed(),
        [InvoiceDisplayStatus.PartiallyRefunded]: texts.partiallyRefunded()
    };
}
export function getPaymentStatusFilterOptions(param) {
    let { isVendor, counters, texts } = param;
    return [
        InvoicePaymentDisplayStatusType.StoppedPayment,
        InvoicePaymentDisplayStatusType.RequiresPaymentApproval,
        InvoicePaymentDisplayStatusType.RequiresPaymentValidation,
        InvoicePaymentDisplayStatusType.PaymentFailed,
        InvoicePaymentDisplayStatusType.PaymentMethodNotSet,
        InvoicePaymentDisplayStatusType.PaymentMethodUnverified,
        InvoicePaymentDisplayStatusType.Processing,
        InvoicePaymentDisplayStatusType.Collected,
        InvoicePaymentDisplayStatusType.Paid,
        InvoicePaymentDisplayStatusType.PaidExternally,
        InvoicePaymentDisplayStatusType.Refunded,
        InvoicePaymentDisplayStatusType.Disputed,
        InvoicePaymentDisplayStatusType.RefundRequested,
        InvoicePaymentDisplayStatusType.None
    ].map((status)=>({
            value: status,
            label: getPaymentStatusFilterText({
                status,
                isVendor,
                texts
            }),
            counter: counters[status] || 0
        })).filter((opt)=>opt.counter);
}
function getPaymentStatusFilterText(param) {
    let { status, isVendor, texts } = param;
    switch(status){
        case InvoicePaymentDisplayStatusType.None:
            return texts.none();
        case InvoicePaymentDisplayStatusType.Collected:
            return texts.collected();
        case InvoicePaymentDisplayStatusType.Paid:
            return texts.deposited();
        case InvoicePaymentDisplayStatusType.PaidExternally:
            return texts.paidExternally();
        case InvoicePaymentDisplayStatusType.PaymentFailed:
            return texts.paymentFailed();
        case InvoicePaymentDisplayStatusType.PaymentMethodNotSet:
            return texts.paymentMethodNotSet();
        case InvoicePaymentDisplayStatusType.PaymentMethodUnverified:
            return texts.paymentMethodNotVerified();
        case InvoicePaymentDisplayStatusType.Pending:
            return texts.issued();
        case InvoicePaymentDisplayStatusType.Processing:
            return texts.paymentInitiated();
        case InvoicePaymentDisplayStatusType.Refunded:
            return texts.refundInitiated();
        case InvoicePaymentDisplayStatusType.RefundFailed:
            return texts.refundFailed();
        case InvoicePaymentDisplayStatusType.RequiresPaymentApproval:
            return isVendor ? texts.clientApprovalRequired() : texts.pendingYourApproval();
        case InvoicePaymentDisplayStatusType.RequiresPaymentValidation:
            return texts.validationRequired();
        case InvoicePaymentDisplayStatusType.StoppedPayment:
            return texts.clientStoppedPayment();
        case InvoicePaymentDisplayStatusType.Disputed:
            return texts.disputed();
        case InvoicePaymentDisplayStatusType.RefundRequested:
            return texts.refundRequested();
    }
}
export function getInvoiceStatusFilterOptions(param) {
    let { counters, texts } = param;
    const labels = invoiceDisplayStatusToLabel(texts);
    return [
        InvoiceDisplayStatus.Issued,
        InvoiceDisplayStatus.PaymentInitiated,
        InvoiceDisplayStatus.Paid,
        InvoiceDisplayStatus.Overdue,
        InvoiceDisplayStatus.Voided,
        InvoiceDisplayStatus.Refunded,
        InvoiceDisplayStatus.RefundFailed,
        InvoiceDisplayStatus.PaymentDisputed,
        InvoiceDisplayStatus.PartiallyRefunded
    ].map((statusId)=>({
            value: statusId,
            label: labels[statusId],
            counter: counters[statusId] || 0
        })).filter((opt)=>opt.counter);
}
function invoiceDisplayStatusV2ToLabel(texts) {
    return {
        [InvoiceDisplayStatusV2.Issued]: texts.issued(),
        [InvoiceDisplayStatusV2.Pending]: texts.pending(),
        [InvoiceDisplayStatusV2.Overdue]: texts.overdue(),
        [InvoiceDisplayStatusV2.Processing]: texts.processing(),
        [InvoiceDisplayStatusV2.Paid]: texts.paid(),
        [InvoiceDisplayStatusV2.Voided]: texts.voided(),
        [InvoiceDisplayStatusV2.Refunded]: texts.refunded(),
        [InvoiceDisplayStatusV2.RefundFailed]: texts.refundFailed(),
        [InvoiceDisplayStatusV2.Disputed]: texts.disputed(),
        [InvoiceDisplayStatusV2.PartiallyRefunded]: texts.partiallyRefunded()
    };
}
export function getInvoiceStatusV2FilterOptions(param) {
    let { counters, texts } = param;
    const labels = invoiceDisplayStatusV2ToLabel(texts);
    return [
        InvoiceDisplayStatusV2.Issued,
        InvoiceDisplayStatusV2.Pending,
        InvoiceDisplayStatusV2.Overdue,
        InvoiceDisplayStatusV2.Processing,
        InvoiceDisplayStatusV2.Paid,
        InvoiceDisplayStatusV2.Voided,
        InvoiceDisplayStatusV2.Refunded,
        InvoiceDisplayStatusV2.RefundFailed,
        InvoiceDisplayStatusV2.Disputed,
        InvoiceDisplayStatusV2.PartiallyRefunded
    ].map((statusId)=>({
            value: statusId,
            label: labels[statusId],
            counter: counters[statusId] || 0
        })).filter((opt)=>opt.counter);
}
export function getIssuesPreventingPaymentFilterOptions(param) {
    let { counters, texts } = param;
    return ISSUES_PREVENTING_PAYMENT.map((type)=>({
            value: type,
            label: getIssuePreventingPaymentLabel(type, texts),
            counter: counters[type] || 0
        })).filter((opt)=>opt.counter);
}
function getIssuePreventingPaymentLabel(type, texts) {
    switch(type){
        case PaymentDetailType.MissingPaymentMethod:
            return texts.missingPaymentMethod();
        case PaymentDetailType.ClientApprovalRequired:
            return texts.clientApprovalRequired();
        case PaymentDetailType.PaymentFailed:
            return texts.paymentFailedFilter();
        case PaymentDetailType.PaymentValidationRequired:
            return texts.paymentValidationRequired();
        case PaymentDetailType.PaymentStopped:
            return texts.paymentStoppedFilter();
        case PaymentDetailType.PaymentMethodNotVerified:
            return texts.paymentMethodNotVerified();
        default:
            return '';
    }
}
export function getProcessedByFilterOptions(param) {
    let { counters, texts } = param;
    return [
        {
            value: 'anchor',
            label: texts.anchor(),
            counter: counters[PaymentDetailType.PaidOnDate] || 0
        },
        {
            value: 'external',
            label: texts.external(),
            counter: counters[PaymentDetailType.PaidExternally] || 0
        }
    ].filter((opt)=>opt.counter);
}
export function getAccountingStatusFilterOptions(param) {
    let { counters, texts } = param;
    const syncedCounter = counters.sync || 0;
    const unsyncedCounter = counters.unsync || 0;
    return [
        {
            value: AccountingStatusFilter.All,
            label: texts.all(),
            counter: syncedCounter + unsyncedCounter
        },
        {
            value: AccountingStatusFilter.Synced,
            label: texts.synced(),
            counter: syncedCounter
        },
        {
            value: AccountingStatusFilter.NotSynced,
            label: texts.notSynced(),
            counter: unsyncedCounter
        }
    ].filter((opt)=>opt.counter);
}
export function applyOptimisticUpdate(invoices, optimisticUpdate, now) {
    const { type, invoiceId } = optimisticUpdate;
    const updateInvoice = (update)=>invoices.map((invoice)=>invoice.id === invoiceId ? update(invoice) : invoice);
    switch(type){
        case InvoiceOptimisticUpdateType.Refunded:
            return updateInvoice((inv)=>invoiceModel.setRefunded(inv, optimisticUpdate.timestamp, now));
        case InvoiceOptimisticUpdateType.RefundRequested:
            {
                const { userId, reason } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setRefundRequested(inv, userId, reason));
            }
        case InvoiceOptimisticUpdateType.Voided:
            return updateInvoice((inv)=>invoiceModel.setVoided(inv, optimisticUpdate.userId));
        case InvoiceOptimisticUpdateType.PaymentApproved:
            return updateInvoice((inv)=>invoiceModel.setPending(inv, now));
        case InvoiceOptimisticUpdateType.PaymentConfirmed:
            {
                const { isVendor } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setPaymentConfirmed(inv, isVendor));
            }
        case InvoiceOptimisticUpdateType.QboIntegrationChanged:
            {
                const { integration } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setIntegration(inv, integration));
            }
        case InvoiceOptimisticUpdateType.XeroIntegrationChanged:
            {
                const { xeroIntegration } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setXeroIntegration(inv, xeroIntegration));
            }
        case InvoiceOptimisticUpdateType.PartialRefundRequested:
            {
                const { userId, reason, refundAmount, refundFees } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setPartialRefundRequested({
                        invoice: inv,
                        userId,
                        reason,
                        refundAmount,
                        refundFees
                    }));
            }
        case InvoiceOptimisticUpdateType.PartiallyRefunded:
            {
                const { timestamp, fees, paymentProcessorType, reason, userId, refundAmount, refundFees, rawTotalAmount } = optimisticUpdate;
                return updateInvoice((inv)=>invoiceModel.setPartiallyRefunded(inv, {
                        timestamp,
                        fees,
                        paymentProcessorType,
                        reason,
                        userId,
                        refundAmount,
                        refundFees,
                        rawTotalAmount
                    }));
            }
        case InvoiceOptimisticUpdateType.Changed:
            {
                const { invoice } = optimisticUpdate;
                return updateInvoice((_inv)=>invoice);
            }
    }
}
export function mapSortAndFiltersRequestParams(param) {
    let { date, now, sort, sortIds, search, status, displayStatusV2, paymentStatus, qbo, xero, paymentDetails } = param;
    const { fromDate, toDate } = getFormattedTimeRange(date, now);
    return {
        search,
        status,
        displayStatusV2,
        paymentStatus,
        paymentDetails: paymentDetails && paymentDetails.length > 0 ? paymentDetails : undefined,
        integration: qbo,
        xeroIntegration: xero,
        issuedOnSince: fromDate ? new Date(fromDate) : undefined,
        issuedOnUntil: toDate ? new Date(toDate) : undefined,
        sort: sort ? invoiceHeaderItemIdToSortingColumn[sortIds[sort.colIndex]] ? {
            field: invoiceHeaderItemIdToSortingColumn[sortIds[sort.colIndex]],
            type: sort.sort
        } : undefined : undefined
    };
}
export function getTableDefs(param) {
    let { showQboIntegration, hideDueDate, showXeroIntegration, texts, INVOICE_STATUSES_V2, AD_HOC_INVOICING } = param;
    const issueDateLabel = texts.issueDate();
    const totalLabel = texts.total();
    const tableDefs = compact([
        {
            headerItem: {
                id: "invoiceId",
                value: texts.invoiceId()
            },
            columnDef: {
                width: showQboIntegration || showXeroIntegration ? 140 : 100,
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_invoice_id'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "companyName",
                value: texts.companyName()
            },
            columnDef: {
                width: [
                    150,
                    200
                ],
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_company_name'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "status",
                value: texts.invoiceStatus()
            },
            columnDef: {
                width: [
                    96,
                    140
                ],
                sortable: true,
                mobile: {},
                bi: {
                    name: 'sort_invoice_status'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "additionalInformation",
                value: INVOICE_STATUSES_V2 ? texts.paymentDetails() : texts.paymentStatus()
            },
            columnDef: {
                width: [
                    160,
                    220
                ],
                sortable: !INVOICE_STATUSES_V2,
                bi: {
                    name: 'sort_payment_status'
                }
            },
            defaultSortDirection: 'asc'
        },
        AD_HOC_INVOICING && {
            headerItem: {
                id: "paymentMethod",
                value: texts.paymentMethod()
            },
            columnDef: {
                width: [
                    100,
                    140
                ],
                sortable: false,
                bi: {
                    name: 'payment_method'
                }
            },
            defaultSortDirection: 'asc'
        },
        {
            headerItem: {
                id: "issueDate",
                value: issueDateLabel
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                bi: {
                    name: 'sort_issue_date'
                },
                mobile: {
                    label: {
                        text: issueDateLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        },
        !hideDueDate && {
            headerItem: {
                id: "dueDate",
                value: texts.dueDate()
            },
            columnDef: {
                width: [
                    80,
                    100
                ],
                sortable: true,
                bi: {
                    name: 'sort_due_date'
                }
            },
            defaultSortDirection: 'desc'
        },
        {
            headerItem: {
                id: "total",
                value: totalLabel
            },
            columnDef: {
                width: 84,
                sortable: true,
                hAlign: 'end',
                bi: {
                    name: 'sort_total'
                },
                mobile: {
                    label: {
                        text: totalLabel
                    }
                }
            },
            defaultSortDirection: 'desc'
        }
    ]);
    const headerItems = tableDefs.map((d)=>d.headerItem);
    const columnDefs = tableDefs.map((d)=>d.columnDef);
    const sortIds = headerItems.map((item)=>item.id);
    const sortOptions = tableDefs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
            {
                id: d.headerItem.id,
                label: String(d.headerItem.value),
                defaultDirection: d.defaultSortDirection,
                colIndex
            }
        ] : []);
    return {
        columnDefs,
        headerItems,
        sortIds,
        sortOptions
    };
}
